import React, { FC } from 'react';
import styled from 'styled-components';
import {
    SkeletonBlock,
    SkeletonFiltersRow,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonRow,
    SkeletonStack,
    SkeletonSurface,
    SkeletonTable,
    SkeletonTableRow
} from 'src/components/widgets/skeleton';

const SalesChannelsShell = styled(SkeletonPageShell).attrs({ $padding: '60px 0 48px' })``;

const TasksShell = styled(SkeletonPageShell).attrs({ $padding: '20px 0 48px' })``;

const SectionSurfaceSkeleton: FC<{ titleWidth: string; descriptionWidth: string; rows: number; actionWidths?: string[] }> = ({
    titleWidth,
    descriptionWidth,
    rows,
    actionWidths = []
}) => (
    <SkeletonSurface $padding='20px 24px' $radius='8px'>
        <SkeletonStack $gap='16px'>
            <SkeletonRow $gap='16px' $justify='space-between'>
                <SkeletonStack $gap='6px' $flex='1'>
                    <SkeletonBlock $width={titleWidth} $height='16px' />
                    <SkeletonBlock $width={descriptionWidth} $height='13px' />
                </SkeletonStack>
                <SkeletonRow $gap='10px' $justify='flex-end'>
                    {actionWidths.map((width, index) => (
                        <SkeletonBlock key={`${width}-${index}`} $width={width} $height='36px' $radius='6px' />
                    ))}
                </SkeletonRow>
            </SkeletonRow>
            <SalesChannelsTableSkeleton rows={rows} />
        </SkeletonStack>
    </SkeletonSurface>
);

const TaskContentWrapperSkeleton = styled.div`
    padding: 24px 30px 30px 30px;
    background-color: ${(props) => props.theme.app.backgroundColorBase};
`;

const DetailsGridSkeleton = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 24px;
    min-width: 0;

    @media (max-width: 968px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const DetailRowSkeleton = styled(SkeletonRow).attrs({ $justify: 'space-between' })`
    padding: 12px 0;
    border-bottom: 1px solid #e0e0e0;

    &:last-child {
        border-bottom: none;
    }
`;

const PageHeaderSkeleton: FC<{ titleWidth: string; descriptionWidth?: string; actionWidths?: string[] }> = ({
    titleWidth,
    descriptionWidth,
    actionWidths = []
}) => (
    <SkeletonPageHeader>
        <SkeletonStack $gap='8px' $flex='1'>
            <SkeletonBlock $width={titleWidth} $height='26px' />
            {descriptionWidth && <SkeletonBlock $width={descriptionWidth} $height='13px' />}
            {descriptionWidth && <SkeletonBlock $width='min(180px, 100%)' $height='13px' />}
        </SkeletonStack>
        {actionWidths.length > 0 && (
            <SkeletonRow $gap='10px'>
                {actionWidths.map((width, index) => (
                    <SkeletonBlock key={`${width}-${index}`} $width={width} $height='36px' $radius='6px' />
                ))}
            </SkeletonRow>
        )}
    </SkeletonPageHeader>
);

const SalesChannelsTableSkeleton: FC<{ rows: number }> = ({ rows }) => (
    <SkeletonTable>
        <SkeletonTableRow
            $columns='0.73fr 1.21fr minmax(180px, 500px) 0.5fr 1.14fr'
            $mobileColumns='minmax(0, 1fr) 90px'
            $mobileBreakpoint='900px'
            $padding='10px 14px'
            $minHeight='40px'
        >
            <SkeletonBlock $width='18px' $height='13px' />
            <SkeletonBlock $width='82px' $height='13px' />
            <SkeletonBlock $width='34px' $height='13px' />
            <SkeletonBlock $width='58px' $height='13px' />
            <SkeletonBlock $width='1px' $height='1px' />
        </SkeletonTableRow>
        {Array.from({ length: rows }).map((_, index) => (
            <SkeletonTableRow
                key={index}
                $columns='0.73fr 1.21fr minmax(180px, 500px) 0.5fr 1.14fr'
                $mobileColumns='minmax(0, 1fr) 90px'
                $mobileBreakpoint='900px'
                $padding='10px 14px'
                $minHeight='52px'
            >
                <SkeletonBlock $width='72px' $height='15px' />
                <SkeletonBlock $width={index % 2 === 0 ? '130px' : '170px'} $height='15px' />
                <SkeletonBlock $width='min(360px, 100%)' $height='15px' />
                <SkeletonBlock $width='32px' $height='32px' $radius='6px' />
                <SkeletonRow $gap='12px' $justify='flex-end'>
                    <SkeletonBlock $width='78px' $height='15px' />
                    <SkeletonBlock $width='24px' $height='24px' $radius='4px' />
                </SkeletonRow>
            </SkeletonTableRow>
        ))}
    </SkeletonTable>
);

const SwitchTasksTableSkeleton: FC = () => (
    <SkeletonTable>
        <SkeletonTableRow $columns='0.5fr 1fr 1fr 1fr 1.5fr 1.2fr' $mobileColumns='minmax(0, 1fr) 110px' $mobileBreakpoint='900px'>
            <SkeletonBlock $width='58px' $height='13px' />
            <SkeletonBlock $width='52px' $height='13px' />
            <SkeletonBlock $width='42px' $height='13px' />
            <SkeletonBlock $width='56px' $height='13px' />
            <SkeletonBlock $width='112px' $height='13px' />
            <SkeletonBlock $width='62px' $height='13px' />
        </SkeletonTableRow>
        {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonTableRow key={index} $columns='0.5fr 1fr 1fr 1fr 1.5fr 1.2fr' $mobileColumns='minmax(0, 1fr) 110px' $mobileBreakpoint='900px'>
                <SkeletonBlock $width='64px' $height='15px' />
                <SkeletonBlock $width='104px' $height='22px' $radius='11px' />
                <SkeletonBlock $width='min(150px, 100%)' $height='15px' />
                <SkeletonBlock $width='min(150px, 100%)' $height='15px' />
                <SkeletonBlock $width={index % 2 === 0 ? '110px' : '88px'} $height='15px' />
                <SkeletonBlock $width='92px' $height='30px' $radius='6px' />
            </SkeletonTableRow>
        ))}
    </SkeletonTable>
);

const TaskDetailsSectionSkeleton: FC<{ titleWidth: string; rows?: number }> = ({ titleWidth, rows = 3 }) => (
    <SkeletonSurface $padding='20px' $radius='8px'>
        <SkeletonStack $gap='12px'>
            <SkeletonBlock $width={titleWidth} $height='18px' />
            {Array.from({ length: rows }).map((_, index) => (
                <DetailRowSkeleton key={index}>
                    <SkeletonBlock $width={index % 2 === 0 ? '118px' : '150px'} $height='14px' />
                    <SkeletonBlock $width={index % 2 === 0 ? '96px' : '128px'} $height='14px' />
                </DetailRowSkeleton>
            ))}
        </SkeletonStack>
    </SkeletonSurface>
);

export const SalesChannelsPageSkeleton: FC = () => (
    <SalesChannelsShell aria-hidden='true'>
        <PageHeaderSkeleton titleWidth='min(220px, 100%)' descriptionWidth='min(560px, 100%)' />
        <SkeletonStack $gap='24px'>
            <SectionSurfaceSkeleton titleWidth='112px' descriptionWidth='min(640px, 100%)' rows={3} actionWidths={['150px', '110px', '130px']} />
            <SectionSurfaceSkeleton titleWidth='82px' descriptionWidth='min(560px, 100%)' rows={2} actionWidths={['130px']} />
        </SkeletonStack>
    </SalesChannelsShell>
);

export const SalesChannelSwitchTasksPageSkeleton: FC = () => (
    <TasksShell aria-hidden='true'>
        <PageHeaderSkeleton titleWidth='min(330px, 100%)' descriptionWidth='min(360px, 100%)' actionWidths={['92px']} />
        <SkeletonFiltersRow showSearch={false} controlWidths={['200px']} $margin='0 0 16px' />
        <TaskContentWrapperSkeleton>
            <SwitchTasksTableSkeleton />
        </TaskContentWrapperSkeleton>
    </TasksShell>
);

export const SalesChannelSwitchTaskDetailPageSkeleton: FC = () => (
    <TasksShell aria-hidden='true'>
        <PageHeaderSkeleton titleWidth='min(390px, 100%)' descriptionWidth='min(360px, 100%)' actionWidths={['92px']} />
        <TaskContentWrapperSkeleton>
            <SkeletonStack $gap='24px'>
                <TaskDetailsSectionSkeleton titleWidth='112px' rows={6} />
                <SkeletonSurface $padding='20px' $radius='8px'>
                    <SkeletonStack $gap='16px'>
                        <SkeletonBlock $width='140px' $height='18px' />
                        <SkeletonBlock $height='12px' $radius='6px' />
                        <SkeletonBlock $height='58px' $radius='8px' />
                    </SkeletonStack>
                </SkeletonSurface>
                <DetailsGridSkeleton>
                    <TaskDetailsSectionSkeleton titleWidth='132px' rows={2} />
                    <TaskDetailsSectionSkeleton titleWidth='190px' rows={2} />
                </DetailsGridSkeleton>
                <TaskDetailsSectionSkeleton titleWidth='108px' rows={6} />
            </SkeletonStack>
        </TaskContentWrapperSkeleton>
    </TasksShell>
);
